import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type JSX,
} from "react";
import {
  FloorPlanGenerationFeedbackDialog,
  FloorPlanGenerationFeedbackDialogProps,
} from "./dialogs/generate-floor-plan-feedback-dialog";
import { FeedbackDialogBaseProps } from "./feedback-types";

/** All supported feedback form types. */
export enum FeedbackKey {
  floorPlanGeneration = "floorPlanGeneration",
}

/** Map of all FeedbackKeys to their required data. */
type FeedbackDataMap = {
  [FeedbackKey.floorPlanGeneration]: FloorPlanGenerationFeedbackDialogProps;
};

function getComponentForFeedback(
  { key, data }: RequestFeedbackProps<FeedbackKey>,
  baseProps: FeedbackDialogBaseProps,
): JSX.Element {
  switch (key) {
    // Eslint complains because this is the only switch value, but we want it to complain, once more feedback types are added.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    case FeedbackKey.floorPlanGeneration:
      return <FloorPlanGenerationFeedbackDialog {...data} {...baseProps} />;
  }
}

/** Props needed to show a feedback dialog. */
type RequestFeedbackProps<T extends FeedbackKey> = {
  /** The FeedbackKey for the kind fo feedback dialog to show. */
  key: T;

  /** The props that the particular feedback component is expecting. */
  data: Omit<FeedbackDataMap[T], "onClose">;
};

/**
 * The FeedbackContext allows you to request feedback from anywhere in the app.
 */
export type FeedbackContext = {
  /** Request feedback from the user. Returns whether the user has given feedback, after the dialog was closed. */
  requestFeedback<T extends FeedbackKey>(
    props: RequestFeedbackProps<T>,
  ): Promise<boolean>;
};

/** Context which provides the file upload functionalities */
export const FeedbackContext = createContext<FeedbackContext | undefined>(
  undefined,
);

/**
 * @returns A context to request feedback from the user.
 */
export function useFeedbackContext(): FeedbackContext {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error("FeedbackContext is not initialized.");
  }
  return context;
}

/**
 * @returns a provider component for the FeedbackContext
 */
export function FeedbackContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const [visibleFeedback, setVisibleFeedback] = useState<JSX.Element>();

  const requestFeedback = useCallback(
    <T extends FeedbackKey>(props: RequestFeedbackProps<T>) =>
      new Promise<boolean>((resolve) => {
        setVisibleFeedback(
          getComponentForFeedback(props, {
            onClose(hasGivenFeedback: boolean) {
              setVisibleFeedback(undefined);
              resolve(hasGivenFeedback);
            },
          }),
        );
      }),
    [],
  );

  const value = useMemo(() => ({ requestFeedback }), [requestFeedback]);

  return (
    <FeedbackContext value={value}>
      {children}
      {visibleFeedback}
    </FeedbackContext>
  );
}
